import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Calendar, Eye, Droplet, Wind, Wand2, Shield, CheckCircle, ArrowRight
} from 'lucide-react';
import { HERO_DATA } from '../data/mockData';
import SEO from '../components/SEO';

const Method = () => {
  const processSteps = [
    {
      number: '1',
      icon: Eye,
      title: 'Inspection and Goals',
      description: 'We begin with a walk-around, discuss the vehicle condition, and identify the defects or areas that matter most to you. Paint correction and coating recommendations are made after inspection.'
    },
    {
      number: '2',
      icon: Droplet,
      title: 'Paint-Safe Wash and Decontamination',
      description: 'Loose contamination is removed before contact washing. Deionized water, clean tools, and brand-new exterior microfiber towels help reduce water spotting and avoid reintroducing debris to the paint.'
    },
    {
      number: '3',
      icon: Wind,
      title: 'Forced-Air Drying and Preparation',
      description: 'Forced air clears water from tight areas and reduces repeated towel contact. When correction or coating is scheduled, the paint is prepared and inspected under appropriate lighting.'
    },
    {
      number: '4',
      icon: Wand2,
      title: 'Correction Matched to the Paint',
      description: 'Polishing is selected around the paint condition and the realistic result. The goal is to improve clarity and reduce visible defects while preserving as much clear coat as practical.'
    },
    {
      number: '5',
      icon: Shield,
      title: 'Protection Matched to the Owner',
      description: 'Sealant or ceramic protection is chosen based on the vehicle, storage, driving habits, and maintenance plan—not simply the longest advertised term.'
    },
    {
      number: '6',
      icon: CheckCircle,
      title: 'Final Inspection and Care Guidance',
      description: 'The vehicle is checked before delivery, and the customer receives clear guidance for washing, curing, and maintaining the finish.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Paint-Safe Detailing Method | Detailing Disciples"
        description="See the paint-safe process behind Detailing Disciples, including deionized water, fresh exterior towels, forced-air drying and careful inspection."
        canonical="https://thedetailingdisciples.com/method"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              The Detailing Disciples <span className="text-[#B8860B]">Paint-Safe Method</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Premium results come from careful decisions at every stage. The Detailing Disciples method is designed to reduce unnecessary contact, identify the right level of correction, and protect the finish after the work is complete.
            </p>
          </div>
        </div>
      </section>

      {/* 6-Step Process */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-8">
            {processSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all"
                >
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    {/* Step Number */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8860B] to-[#C8960B] flex items-center justify-center">
                        <span className="text-[#111216] font-bold text-3xl">{step.number}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#B8860B]/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-[#B8860B]" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">{step.title}</h2>
                      </div>
                      <p className="text-gray-300 text-lg leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              What Makes This <span className="text-[#B8860B]">Different</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-3">Brand-New Exterior Towels</h3>
                <p className="text-gray-400">
                  We use brand-new microfiber towels on painted exterior surfaces to reduce the risk of transferring trapped debris back onto your finish.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-3">Deionized Water</h3>
                <p className="text-gray-400">
                  Spot-reducing rinse water helps leave a cleaner finish, especially important in Southern California hard water conditions.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-3">Forced-Air Drying</h3>
                <p className="text-gray-400">
                  Air moves water out of mirrors, grilles, trim, wheels, and tight areas while reducing unnecessary paint contact.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-3">Owner-Operated</h3>
                <p className="text-gray-400">
                  The person you speak with is the person responsible for the work and the final inspection. No handoffs, no miscommunication.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              What We <span className="text-[#B8860B]">Do Not</span> Do
            </h2>
            <div className="bg-red-900/10 border border-red-900/30 rounded-2xl p-8">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold text-xl">✗</span>
                  <span>Rush through appointments to fit more cars in a day</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold text-xl">✗</span>
                  <span>Reuse towels from previous vehicles on your paint</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold text-xl">✗</span>
                  <span>Force every vehicle into the same one-size-fits-all package</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold text-xl">✗</span>
                  <span>Promise perfect paint or guarantee every scratch will disappear</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold text-xl">✗</span>
                  <span>Apply ceramic coating without proper paint preparation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Request a Personalized Recommendation
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Share your vehicle details and goals. We will assess the condition and recommend the right services based on what you want to achieve.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request Your Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`}>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#111216] text-[#111216] hover:bg-[#111216] hover:text-white font-semibold"
              >
                Call {HERO_DATA.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Method;
